function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
var dispatchCustomEvent = function dispatchCustomEvent(eventName) {
  var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var detail = { detail: data };
  var event = new CustomEvent(eventName, data ? detail : null);
  document.dispatchEvent(event);
};
window.recentlyViewedIds = [];

const FETCH_CONFIG = {
  method: 'POST',
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Accept': 'application/javascript'
  }
};

const OverlayManager = {
  stack: [],
  beforeCloseHandlers: new Map(),
  cc: null,
  getCC() {
    if (!this.cc) this.cc = document.querySelector('.click-capture');
    return this.cc;
  },
  updateCCZIndex() {
    let cc = this.getCC();
    if (!cc) return;
    if (this.stack.length > 1) {
      let topPanel = this.stack[this.stack.length - 1];
      let topZ = parseInt(getComputedStyle(topPanel).zIndex, 10) || 90;
      cc.style.zIndex = topZ - 1;
    } else {
      cc.style.zIndex = '';
    }
  },
  open(panel) {
    if (!panel) return;
    const index = this.stack.indexOf(panel);
    if (index > -1) this.stack.splice(index, 1);
    this.stack.push(panel);
    panel.classList.add('active');
    panel.inert = false;
    document.body.classList.add('open-cc');
    this.updateCCZIndex();
    panel.querySelector('.side-panel-close')?.focus();
    dispatchCustomEvent('panel:open', { panel });
  },
  close(panel) {
    if (!panel) return;
    const index = this.stack.indexOf(panel);
    if (index > -1) this.stack.splice(index, 1);
    if (!this.stack.length) document.body.classList.remove('open-cc');
    this.updateCCZIndex();
    panel.classList.remove('active');
    panel.inert = true;
    dispatchCustomEvent('panel:closed', { panel });
    const handler = this.beforeCloseHandlers.get(panel);
    if (handler) handler();
  },
  closeLast() {
    const panel = this.stack[this.stack.length - 1];
    if (panel) this.close(panel);
  },
  registerBeforeClose(panel, handler) {
    this.beforeCloseHandlers.set(panel, handler);
  },
  closeAll() {
    while (this.stack.length) this.closeLast();
  },
  isOpen(panel) { return this.stack.includes(panel); },
  hasOpen() { return this.stack.length > 0; }
};

if (!customElements.get('quantity-selector')) {
  class QuantityInput extends HTMLElement {
    constructor() { super(); }
    connectedCallback() {
      this.input = this.querySelector('.qty');
      this.step = this.input.getAttribute('step');
      this.changeEvent = new Event('change', { bubbles: true });
      this.subtract = this.querySelector('.minus');
      this.add = this.querySelector('.plus');
      this.subtract.addEventListener('click', () => this.change_quantity(-1 * this.step));
      this.add.addEventListener('click', () => this.change_quantity(1 * this.step));
      this.validateQtyRules();
      this.classList.add('buttons_added');
    }
    change_quantity(change) {
      let quantity = Number(this.input.value);
      if (isNaN(quantity)) quantity = 1;
      if (this.input.getAttribute('min') > (quantity + change)) return;
      if (this.input.getAttribute('max') && this.input.getAttribute('max') < (quantity + change)) return;
      quantity += change;
      quantity = Math.max(quantity, 1);
      this.input.value = quantity;
      this.input.dispatchEvent(this.changeEvent);
      this.validateQtyRules();
    }
    validateQtyRules() {
      const value = parseInt(this.input.value);
      if (this.input.min) this.subtract.classList.toggle('disabled', value <= parseInt(this.input.min));
      if (this.input.max) this.add.classList.toggle('disabled', value >= parseInt(this.input.max));
    }
  }
  customElements.define('quantity-selector', QuantityInput);
}

if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() { super(); }
    connectedCallback() {
      this.swatches = this.querySelector('.product-card-swatches');
      this.image = this.querySelector('.product-featured-image-link .product-primary-image');
      this.additional_images = this.querySelectorAll('.product-secondary-image');
      this.additional_images_nav = this.querySelectorAll('.product-secondary-images-nav li');
      this.quick_add = this.querySelector('.product-card--add-to-cart-button-simple');
      if (this.swatches) this.enableSwatches(this.swatches, this.image);
      if (this.additional_images.length) this.enableAdditionalImages();
      if (this.quick_add) this.enableQuickAdd();
    }
    enableAdditionalImages() {
      let image_length = this.additional_images.length,
        images = this.additional_images,
        nav = this.additional_images_nav,
        image_container = this.querySelector('.product-featured-image'),
        cachedWidth = 0, currentIndex = -1, rafId = null;
      const updateHover = (e) => {
        let sel = Math.floor((e.offsetX / cachedWidth) * image_length);
        if (!Number.isFinite(sel) || sel === currentIndex || sel < 0 || sel >= image_length) return;
        if (currentIndex >= 0) { images[currentIndex].classList.remove('hover'); if (nav.length) nav[currentIndex].classList.remove('active'); }
        currentIndex = sel;
        images[sel].classList.add('hover');
        if (nav.length) nav[sel].classList.add('active');
      };
      const mousemove = (e) => { if (rafId) return; rafId = requestAnimationFrame(() => { rafId = null; updateHover(e); }); };
      const mouseenter = (e) => { cachedWidth = image_container.getBoundingClientRect().width; updateHover(e); };
      const mouseleave = () => {
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
        if (currentIndex >= 0) { images[currentIndex].classList.remove('hover'); if (nav.length) nav[currentIndex].classList.remove('active'); currentIndex = -1; }
      };
      if (image_container) {
        image_container.addEventListener('touchstart', mouseenter, { passive: true });
        image_container.addEventListener('touchmove', mousemove, { passive: true });
        image_container.addEventListener('touchend', mouseleave, { passive: true });
        image_container.addEventListener('mouseenter', mouseenter, { passive: true });
        image_container.addEventListener('mousemove', mousemove, { passive: true });
        image_container.addEventListener('mouseleave', mouseleave, { passive: true });
      }
      if (document.readyState === 'complete') {
        images.forEach(function(image) { lazySizes.loader.unveil(image); });
      } else {
        window.addEventListener('load', () => { images.forEach(function(image) { lazySizes.loader.unveil(image); }); }, { once: true });
      }
    }
    enableSwatches(swatches, image) {
      let swatch_list = swatches.querySelectorAll('.product-card-swatch'),
        org_srcset = image ? image.dataset.srcset : '';
      const unveilSwatches = () => { swatch_list.forEach((swatch) => { let img = new Image(); img.srcset = swatch.dataset.srcset; lazySizes.loader.unveil(img); }); };
      if (document.readyState === 'complete') unveilSwatches();
      else window.addEventListener('load', unveilSwatches, { once: true });
      swatches.addEventListener('mouseover', (e) => {
        let swatch = e.target.closest('.product-card-swatch');
        if (!swatch) return;
        let active = swatches.querySelector('.product-card-swatch.active');
        if (active) active.classList.remove('active');
        swatch.classList.add('active');
        if (image) image.setAttribute('srcset', swatch.dataset.srcset || org_srcset);
      });
      swatches.addEventListener('click', (e) => {
        let swatch = e.target.closest('.product-card-swatch');
        if (!swatch) return;
        e.preventDefault();
        window.location.href = swatch.dataset.href;
      });
    }
    enableQuickAdd() {
      if (this._quickAddBound) return;
      this._quickAddBound = true;
      this.quick_add.addEventListener('click', this.quickAdd.bind(this));
    }
    quickAdd(evt) {
      evt.preventDefault();
      if (this.quick_add.disabled) return;
      this.quick_add.classList.add('loading');
      this.quick_add.setAttribute('aria-disabled', true);
      let formData = new FormData(this.form);
      formData.append('id', this.quick_add.dataset.productId);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      fetch(`${theme.routes.cart_add_url}`, { ...FETCH_CONFIG, body: formData })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) return;
          this.renderContents(response);
          dispatchCustomEvent('cart:item-added', { product: response.hasOwnProperty('items') ? response.items[0] : response });
        })
        .catch((e) => { console.error(e); })
        .finally(() => { this.quick_add.classList.remove('loading'); this.quick_add.removeAttribute('aria-disabled'); });
      return false;
    }
    getSectionsToRender() {
      return [
        { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        const el = document.getElementById(section.id);
        if (!el) return;
        const elementToReplace = el.querySelector(section.selector) || el;
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      }));
      let cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        let productDrawer = document.getElementById('Product-Drawer');
        if (productDrawer && OverlayManager.isOpen(productDrawer)) OverlayManager.close(productDrawer);
        cartDrawer.open();
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }
  }
  customElements.define('product-card', ProductCard);
}

if (!customElements.get('product-card-small')) {
  class ProductCardSmall extends HTMLElement {
    constructor() {
      super();
      this.quick_add_enabled = this.classList.contains('quick-add-to-card--true');
      this.button = this.querySelector('button');
      this.id = this.dataset.id;
      this.url = this.dataset.url;
    }
    connectedCallback() {
      if (this.quick_add_enabled) this.setEventListeners();
    }
    setEventListeners() {
      if (this._bound) return;
      this._bound = true;
      this.button.addEventListener('click', this.addToCart.bind(this));
    }
    getSectionsToRender() {
      return [
        { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }
    addToCart() {
      this.button.classList.add('loading');
      this.button.setAttribute('aria-disabled', true);
      let formData = new FormData(this.form);
      formData.append('id', this.id);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      fetch(`${theme.routes.cart_add_url}`, { ...FETCH_CONFIG, body: formData })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) return;
          this.renderContents(response);
          dispatchCustomEvent('cart:item-added', { product: response.hasOwnProperty('items') ? response.items[0] : response });
        })
        .catch((e) => { console.error(e); })
        .finally(() => { this.button.classList.remove('loading'); this.button.removeAttribute('aria-disabled'); });
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        const el = document.getElementById(section.id);
        if (!el) return;
        const elementToReplace = el.querySelector(section.selector) || el;
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      }));
      let cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        let productDrawer = document.getElementById('Product-Drawer');
        if (productDrawer && OverlayManager.isOpen(productDrawer)) OverlayManager.close(productDrawer);
        cartDrawer.open();
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }
  }
  customElements.define('product-card-small', ProductCardSmall);
}

if (!customElements.get('product-slider')) {
  class ProductSlider extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', this.setupProductGallery);
    }
    connectedCallback() {
      this.product_container = this.closest('.thb-product-detail');
      this.thumbnail_container = this.product_container.querySelector('.product-thumbnail-container');
      this.video_containers = this.querySelectorAll('.product-single__media-external-video--play');
      this.setOptions();
      this.init();
    }
    setOptions() {
      this.hide_variants = this.dataset.hideVariants == 'true';
      this.thumbnails = this.thumbnail_container.querySelectorAll('.product-thumbnail');
      this.prev_button = this.querySelector('.flickity-prev');
      this.next_button = this.querySelector('.flickity-next');
      this.options = { wrapAround: true, pageDots: true, contain: true, adaptiveHeight: true, initialIndex: '.is-initial-selected', prevNextButtons: false, fade: false, cellSelector: '.product-images__slide.is-active' };
    }
    init() {
      this.flkty = new Flickity(this, this.options);
      this.selectedIndex = this.flkty.selectedIndex;
      this.setupEvents();
      this.setupProductGallery();
    }
    reInit(imageSetIndex) {
      this.flkty.destroy();
      this.setOptions();
      this.flkty = new Flickity(this, this.options);
      this.setupEvents();
      this.selectedIndex = this.flkty.selectedIndex;
    }
    setupEvents() {
      const _this = this;
      if (this.prev_button) {
        let prev = this.prev_button.cloneNode(true);
        this.prev_button.parentNode.append(prev);
        this.prev_button.remove();
        prev.addEventListener('click', () => { this.flkty.previous(); });
        prev.addEventListener('keyup', () => { this.flkty.previous(); });
      }
      if (this.next_button) {
        let next = this.next_button.cloneNode(true);
        this.next_button.parentNode.append(next);
        this.next_button.remove();
        next.addEventListener('click', () => { this.flkty.next(); });
        next.addEventListener('keyup', () => { this.flkty.next(); });
      }
      this.video_containers.forEach((container) => {
        container.querySelector('button').addEventListener('click', function() { container.setAttribute('hidden', ''); });
      });
      this.flkty.on('settle', function(index) { _this.selectedIndex = index; });
      this.flkty.on('change', (index) => {
        let previous_slide = this.flkty.cells[_this.selectedIndex].element,
          previous_media = previous_slide.querySelector('.product-single__media'),
          active_thumbs = Array.from(this.thumbnails).filter(element => element.classList.contains('is-active')),
          active_thumb = active_thumbs[index] ? active_thumbs[index] : active_thumbs[0];
        this.thumbnails.forEach((item) => { item.classList.remove('is-initial-selected'); });
        active_thumb.classList.add('is-initial-selected');
        requestAnimationFrame(() => {
          if (active_thumb.offsetParent === null) return;
          const windowHalfHeight = active_thumb.offsetParent.clientHeight / 2,
            windowHalfWidth = active_thumb.offsetParent.clientWidth / 2;
          active_thumb.parentElement.scrollTo({
            left: active_thumb.offsetLeft - windowHalfWidth + active_thumb.clientWidth / 2,
            top: active_thumb.offsetTop - windowHalfHeight + active_thumb.clientHeight / 2,
            behavior: 'smooth'
          });
        });
        if (previous_media.classList.contains('product-single__media-external-video')) {
          if (previous_media.dataset.provider === 'youtube') {
            previous_media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: "" }), "*");
          } else if (previous_media.dataset.provider === 'vimeo') {
            previous_media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({ method: "pause" }), "*");
          }
          previous_media.querySelector('.product-single__media-external-video--play').removeAttribute('hidden');
        } else if (previous_media.classList.contains('product-single__media-native-video')) {
          previous_media.querySelector('video').pause();
        }
      });
      setTimeout(() => {
        let active_thumbs = Array.from(this.thumbnails).filter(element => element.clientWidth > 0);
        active_thumbs.forEach((thumbnail, index) => {
          thumbnail.addEventListener('click', () => { this.thumbnailClick(thumbnail, index); });
        });
      });
    }
    thumbnailClick(thumbnail, index) {
      [].forEach.call(this.thumbnails, function(el) { el.classList.remove('is-initial-selected'); });
      thumbnail.classList.add('is-initial-selected');
      this.flkty.select(index);
    }
    setDraggable(draggable) { this.flkty.options.draggable = draggable; this.flkty.updateDraggable(); }
    selectCell(mediaId) { this.flkty.selectCell(mediaId); }
    setupProductGallery() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) return;
      this.setEventListeners();
      document.addEventListener('product:variant-change', (e) => {
        if (e.detail && e.detail.variantTitle && e.detail.variantTitle !== 'Default Title') {
          this.dataset.variantTitle = e.detail.variantTitle;
        }
      });
    }
    buildItems() {
      this.activeImages = Array.from(this.querySelectorAll('.product-images__slide.is-active .product-single__media-image'));
      return this.activeImages.map((item) => {
        let index = [].indexOf.call(item.parentNode.parentNode.children, item.parentNode);
        let activelink = item.querySelector('.product-single__media-zoom');
        activelink.dataset.index = index;
        return { src: activelink.getAttribute('href'), msrc: activelink.dataset.msrc, w: activelink.dataset.w, h: activelink.dataset.h, thumbSrc: activelink.dataset.thumbSrc || activelink.dataset.msrc };
      });
    }
    setEventListeners() {
      this.links = this.querySelectorAll('.product-single__media-zoom');
      this.pswpElement = document.querySelectorAll('.pswp')[0];
      this.pswpOptions = {
        maxSpreadZoom: 2, loop: true, allowPanToNext: true, closeOnScroll: false, showHideOpacity: false,
        arrowKeys: true, history: false, captionEl: false, fullscreenEl: false, zoomEl: false,
        shareEl: false, counterEl: true, arrowEl: true, preloaderEl: true,
        getThumbBoundsFn: () => {
          const thumbnail = this.querySelector('.product-images__slide.is-selected'),
            pageYScroll = window.pageYOffset || document.documentElement.scrollTop,
            rect = thumbnail.getBoundingClientRect();
          return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
        }
      };
      this.links.forEach((link => { link.addEventListener('click', (e) => this.zoomClick(e, link)); }));
    }
    disconnectedCallback() { if (this.flkty) this.flkty.destroy(); }
    buildPswpThumbnails(items) {
      const container = this.pswpElement.querySelector('.pswp__thumbs-inner');
      if (!container) return;
      container.innerHTML = '';
      if (this.dataset.lightboxThumbnails === 'false' || items.length <= 1) { container.parentElement.style.display = 'none'; return; }
      container.parentElement.style.display = '';
      items.forEach((item, index) => {
        const thumb = document.createElement('div');
        thumb.className = 'pswp__thumb'; thumb.dataset.index = index;
        const img = document.createElement('img');
        img.src = item.thumbSrc; img.alt = ''; img.loading = 'lazy';
        thumb.appendChild(img); container.appendChild(thumb);
      });
    }
    updatePswpProductInfo() {
      const infoEl = this.pswpElement.querySelector('.pswp__product-info');
      if (!infoEl) return;
      if (this.dataset.lightboxProductInfo === 'false') { infoEl.style.display = 'none'; return; }
      infoEl.style.display = '';
      const titleEl = infoEl.querySelector('.pswp__product-title');
      const variantEl = infoEl.querySelector('.pswp__variant-title');
      if (titleEl) titleEl.textContent = this.dataset.productTitle || '';
      if (variantEl) variantEl.textContent = this.dataset.variantTitle || '';
    }
    syncPswpThumbnails(index) {
      const container = this.pswpElement.querySelector('.pswp__thumbs-inner');
      if (!container) return;
      container.querySelectorAll('.pswp__thumb').forEach((t, i) => { t.classList.toggle('is-active', i === index); });
      const activeThumb = container.querySelector('.pswp__thumb.is-active');
      if (activeThumb) activeThumb.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
    }
    zoomClick(e, link) {
      this.items = this.buildItems();
      this.pswpOptions.index = parseInt(link.dataset.index, 10);
      if (typeof PhotoSwipe !== 'undefined') {
        this.buildPswpThumbnails(this.items);
        this.updatePswpProductInfo();
        const bottomBar = this.pswpElement.querySelector('.pswp__bottom-bar');
        if (bottomBar) ['mousedown', 'touchstart', 'pointerdown'].forEach(evtName => { bottomBar.addEventListener(evtName, (evt) => evt.stopPropagation()); });
        let pswp = new PhotoSwipe(this.pswpElement, PhotoSwipeUI_Default, this.items, this.pswpOptions);
        const _this = this;
        document.body.classList.add('open-lightbox');
        pswp.listen('firstUpdate', function() {
          pswp.listen('destroy', function() { document.body.classList.remove('open-lightbox'); });
          pswp.listen('parseVerticalMargin', function(item) { item.vGap = { top: 50, bottom: 140 }; });
        });
        const scrollWrap = this.pswpElement.querySelector('.pswp__scroll-wrap');
        const resetScroll = function() { if (scrollWrap) scrollWrap.scrollLeft = 0; _this.pswpElement.scrollLeft = 0; };
        pswp.listen('beforeChange', function() {
          const index = pswp.getCurrentIndex();
          _this.syncPswpThumbnails(index);
          resetScroll();
          if (_this.flkty) _this.flkty.select(index);
        });
        const thumbsInner = this.pswpElement.querySelector('.pswp__thumbs-inner');
        if (thumbsInner) {
          thumbsInner.addEventListener('click', function handler(evt) {
            evt.stopPropagation();
            const thumb = evt.target.closest('.pswp__thumb');
            if (thumb) { pswp.goTo(parseInt(thumb.dataset.index, 10)); resetScroll(); }
          });
        }
        pswp.listen('close', function() {
          const container = _this.pswpElement.querySelector('.pswp__thumbs-inner');
          if (container) container.innerHTML = '';
        });
        pswp.init();
        this.syncPswpThumbnails(this.pswpOptions.index);
      }
      e.preventDefault();
    }
  }
  customElements.define('product-slider', ProductSlider);
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() { super(); }
    connectedCallback() {
      this.sticky = this.dataset.sticky;
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      this.form.querySelector('[name=id]').disabled = false;
      if (!this.sticky) this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cartNotification = document.querySelector('cart-notification');
      this.body = document.body;
      this.hideErrors = this.dataset.hideErrors === 'true';
    }
    onSubmitHandler(evt) {
      evt.preventDefault();
      if (!this.form.reportValidity()) return;
      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
      submitButtons.forEach((submitButton) => {
        if (submitButton.classList.contains('loading')) return;
        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
      });
      this.handleErrorMessage();
      const config = { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' } };
      let formData = new FormData(this.form);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;
      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            dispatchCustomEvent('product:variant-error', { source: 'product-form', productVariantId: formData.get('id'), errors: response.description, message: response.message });
            if (response.status === 422) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            this.handleErrorMessage(response.description);
            return;
          }
          this.renderContents(response);
          dispatchCustomEvent('cart:item-added', { product: response.hasOwnProperty('items') ? response.items[0] : response });
        })
        .catch((e) => { console.error(e); })
        .finally(() => {
          submitButtons.forEach((submitButton) => { submitButton.classList.remove('loading'); submitButton.removeAttribute('aria-disabled'); });
        });
    }
    getSectionsToRender() {
      return [
        { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) return;
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      }));
      let product_drawer = document.getElementById('Product-Drawer'),
        search_drawer = document.getElementById('Search-Drawer');
      if (search_drawer && OverlayManager.isOpen(search_drawer)) OverlayManager.close(search_drawer);
      if (product_drawer && product_drawer.contains(this)) {
        OverlayManager.close(product_drawer);
        if (document.getElementById('Cart-Drawer')) document.getElementById('Cart-Drawer').open();
      } else if (document.getElementById('Cart-Drawer')) {
        document.getElementById('Cart-Drawer').open();
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }
    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
      if (errorMessage) this.errorMessage.textContent = errorMessage;
    }
  });
}

if (!customElements.get('side-panel-close')) {
  class PanelClose extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => this.close_panel(e));
      if (!PanelClose._initialized) {
        PanelClose._initialized = true;
        document.addEventListener('keyup', (e) => { if (e.code && e.code.toUpperCase() === 'ESCAPE') OverlayManager.closeAll(); });
        let cc = document.querySelector('.click-capture');
        if (cc) cc.addEventListener('click', () => { OverlayManager.closeLast(); });
      }
    }
    close_panel(e) {
      e.preventDefault();
      let panel = e.target.closest('.side-panel');
      if (panel) OverlayManager.close(panel);
    }
  }
  PanelClose._initialized = false;
  customElements.define('side-panel-close', PanelClose);
}

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() { super(); }
    connectedCallback() {
      let button = document.getElementById('cart-drawer-toggle');
      button?.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
      this.debouncedOnChange = debounce((event) => { this.onChange(event); }, 300);
      document.addEventListener('cart:refresh', (event) => { this.refresh(); });
      this.addEventListener('change', this.debouncedOnChange.bind(this));
    }
    onChange(event) {
      if (event.target.classList.contains('qty')) this.updateQuantity(event.target.dataset.index, event.target.value);
    }
    getSectionsToRender() {
      return [
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }
    getSectionInnerHTML(html, selector) {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }
    updateQuantity(line, quantity) {
      this.querySelector(`#CartDrawerItem-${line}`).classList.add('thb-loading');
      const body = JSON.stringify({ line, quantity, sections: this.getSectionsToRender().map((section) => section.section), sections_url: window.location.pathname });
      dispatchCustomEvent('line-item:change:start', { quantity: quantity });
      fetch(`${theme.routes.cart_change_url}`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body })
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);
          this.getSectionsToRender().forEach((section => {
            const el = document.getElementById(section.id);
            if (!el) return;
            const elementToReplace = el.querySelector(section.selector) || el;
            if (parsedState.sections) elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
          }));
          dispatchCustomEvent('line-item:change:end', { quantity: quantity, cart: parsedState });
          this.querySelector(`#CartDrawerItem-${line}`)?.classList.remove('thb-loading');
        });
    }
    open() { OverlayManager.open(this); dispatchCustomEvent('cart-drawer:open'); }
    refresh() {
      fetch(`${window.location.pathname}?sections=cart-drawer,cart-bubble`)
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);
          this.getSectionsToRender().forEach((section => {
            const el = document.getElementById(section.id);
            if (!el) return;
            const elementToReplace = el.querySelector(section.selector) || el;
            elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState[section.section], section.selector);
          }));
        });
    }
  }
  customElements.define('cart-drawer', CartDrawer);
}

if (!customElements.get('custom-select')) {
  class CustomSelect extends HTMLElement {
    constructor() { super(); }
    connectedCallback() {
      this.detailsements = { form: this.closest('form'), input: this.querySelector('input'), button: this.querySelector('.custom-select--button'), panel: this.querySelector('.custom-select--list') };
      this.autoClose = this.dataset.autoClose;
      this.detailsements.button.addEventListener('click', this.toggleSelector.bind(this));
      this.detailsements.button.addEventListener('focusout', this.closeSelector.bind(this));
      this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
      this.detailsements.panel?.querySelectorAll('button').forEach(item => item.addEventListener('click', this.onItemClick.bind(this)));
    }
    hidePanel() { this.detailsements.button.setAttribute('aria-expanded', 'false'); this.classList.remove('custom-select--active'); }
    onContainerKeyUp(event) { if (event.code.toUpperCase() !== 'ESCAPE') return; this.hidePanel(); this.detailsements.button.focus(); }
    onItemClick(event) {
      event.preventDefault();
      this.detailsements.input.value = event.currentTarget.dataset.value;
      this.detailsements.input.dispatchEvent(new Event('change'));
      this.detailsements.button.querySelector('.custom-select--text').textContent = event.currentTarget.textContent;
      if (this.detailsements.form) this.detailsements.form.dispatchEvent(new Event('input'));
      if (this.autoClose) this.classList.remove('custom-select--active');
    }
    toggleSelector(event) {
      event.preventDefault();
      this.classList.toggle('custom-select--active');
      if (this.classList.contains('custom-select--active')) {
        this.detailsements.button.focus();
        this.detailsements.button.setAttribute('aria-expanded', (this.detailsements.button.getAttribute('aria-expanded') === 'false').toString());
      } else { this.hidePanel(); }
    }
    closeSelector(event) {
      const shouldClose = event.relatedTarget && event.relatedTarget.classList.contains('custom-select--button');
      if (event.relatedTarget === null || shouldClose) this.hidePanel();
    }
  }
  customElements.define('custom-select', CustomSelect);
}

if (!customElements.get('localization-form')) {
  class LocalizationForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.inputs = this.form.querySelectorAll('input[name="locale_code"], input[name="country_code"]');
      this.debouncedOnSubmit = debounce((event) => { this.onSubmitHandler(event); }, 200);
      this.inputs.forEach(item => item.addEventListener('change', this.debouncedOnSubmit.bind(this)));
    }
    onSubmitHandler(event) { if (this.form) this.form.submit(); }
  }
  customElements.define('localization-form', LocalizationForm);
}

if (!customElements.get('side-panel-content-tabs')) {
  class SidePanelContentTabs extends HTMLElement {
    constructor() {
      super();
      this.buttons = this.querySelectorAll('button');
      this.panels = this.parentElement.querySelectorAll('.side-panel-content--tab-panel');
    }
    connectedCallback() { this.setupButtonObservers(); }
    disconnectedCallback() {}
    setupButtonObservers() {
      this.buttons.forEach((item, i) => {
        item.addEventListener('click', (e) => { this.toggleActiveClass(i); e.preventDefault(); });
      });
    }
    toggleActiveClass(i) {
      this.buttons.forEach((button) => { button.classList.remove('tab-active'); });
      this.buttons[i].classList.add('tab-active');
      this.panels.forEach((panel) => { panel.classList.remove('tab-active'); });
      this.panels[i].classList.add('tab-active');
    }
  }
  customElements.define('side-panel-content-tabs', SidePanelContentTabs);
}

if (!customElements.get('collapsible-row')) {
  class CollapsibleRow extends HTMLElement {
    constructor() { super(); this.animation = null; this.isClosing = false; this.isExpanding = false; }
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('.collapsible__content');
      this.setListeners();
    }
    setListeners() { this.querySelector('summary').addEventListener('click', (e) => this.onClick(e)); }
    onClick(e) {
      e.preventDefault();
      this.details.style.overflow = 'hidden';
      if (this.isClosing || !this.details.open) this.open();
      else if (this.isExpanding || this.details.open) this.shrink();
    }
    shrink() {
      this.isClosing = true;
      const startHeight = `${this.details.offsetHeight}px`, endHeight = `${this.summary.offsetHeight}px`;
      if (this.animation) this.animation.cancel();
      this.animation = this.details.animate({ height: [startHeight, endHeight] }, { duration: 250, easing: 'ease' });
      this.animation.onfinish = () => this.onAnimationFinish(false);
      this.animation.oncancel = () => this.isClosing = false;
    }
    open() {
      this.details.style.height = `${this.details.offsetHeight}px`;
      this.details.open = true;
      window.requestAnimationFrame(() => this.expand());
    }
    expand() {
      this.isExpanding = true;
      const startHeight = `${this.details.offsetHeight}px`, endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;
      if (this.animation) this.animation.cancel();
      this.animation = this.details.animate({ height: [startHeight, endHeight] }, { duration: 400, easing: 'ease-out' });
      this.animation.onfinish = () => this.onAnimationFinish(true);
      this.animation.oncancel = () => this.isExpanding = false;
    }
    onAnimationFinish(open) {
      this.details.open = open; this.animation = null; this.isClosing = false; this.isExpanding = false;
      this.details.style.height = this.details.style.overflow = '';
    }
  }
  customElements.define('collapsible-row', CollapsibleRow);
}

class ProductRecommendations extends HTMLElement {
  constructor() { super(); }
  fetchProducts() {
    fetch(this.dataset.url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');
        if (recommendations && recommendations.innerHTML.trim().length) this.innerHTML = recommendations.innerHTML;
        this.classList.add('product-recommendations--loaded');
      })
      .catch(e => { console.error(e); });
  }
  connectedCallback() { this.fetchProducts(); }
}
customElements.define('product-recommendations', ProductRecommendations);

if (!customElements.get('quick-view')) {
  const _cache = new Map();
  class QuickView extends HTMLElement {
    constructor() { super(); }
    connectedCallback() {
      this.drawer = document.getElementById('Product-Drawer');
      this.drawerContent = this.drawer.querySelector('#Product-Drawer-Content');
      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);
      OverlayManager.registerBeforeClose(this.drawer, () => {
        document.body.classList.remove('open-cc--product');
        setTimeout(() => { if (!this.drawer.classList.contains('active')) this.drawerContent.innerHTML = ''; }, 260);
      });
    }
    disconnectedCallback() { this.removeEventListener('click', this._onClick); }
    _onClick(e) {
      e.preventDefault();
      let productHandle = this.dataset.productHandle;
      if (!productHandle || this.classList.contains('loading')) return;
      let href = `${theme.routes.root_url}/products/${productHandle}?view=quick-view`;
      href = href.replace('//', '/');
      if (_cache.has(productHandle)) { this.renderQuickview(_cache.get(productHandle), href, productHandle); return; }
      this.classList.add('loading');
      fetch(href)
        .then((response) => response.text())
        .then((text) => {
          this.classList.remove('loading');
          const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html').querySelector('#Product-Drawer-Content').innerHTML;
          _cache.set(productHandle, sectionInnerHTML);
          this.renderQuickview(sectionInnerHTML, href, productHandle);
        })
        .catch(() => { this.classList.remove('loading'); });
    }
    _reloadScripts(container) {
      let scripts = container.querySelectorAll('script'), head = document.head;
      scripts.forEach((oldScript) => {
        let newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => { newScript.setAttribute(attr.name, attr.value); });
        if (oldScript.src) { if (document.querySelector(`script[src="${oldScript.src}"]`)) return; }
        else newScript.textContent = oldScript.textContent;
        if (oldScript.parentNode) oldScript.parentNode.replaceChild(newScript, oldScript);
        else head.appendChild(newScript);
      });
    }
    renderQuickview(sectionInnerHTML, href, productHandle) {
      if (!sectionInnerHTML) return;
      let drawerContent = this.drawer.querySelector('#Product-Drawer-Content');
      drawerContent.innerHTML = sectionInnerHTML;
      this._reloadScripts(drawerContent);
      OverlayManager.open(this.drawer);
      document.body.classList.add('open-cc--product');
      requestAnimationFrame(() => {
        if (Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
        if (window.ProductModel) window.ProductModel.loadShopifyXR();
        let slider = this.drawer.querySelector('#Product-Slider');
        if (slider) slider.reInit();
        window.dispatchEvent(new Event('resize'));
      });
      dispatchCustomEvent('quick-view:open', { productUrl: href, productHandle: productHandle });
      addIdToRecentlyViewed(productHandle);
    }
  }
  customElements.define('quick-view', QuickView);
}

class AnimatedMarkers {
  constructor() {
    this.markers = document.querySelectorAll('.svg-marker path');
    this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
    if (this.animations_enabled && this.markers.length) this.setupEventListeners();
  }
  setupEventListeners() {
    this.markers.forEach((marker) => {
      gsap.from(marker, { duration: 1, ease: 'power4.inOut', drawSVG: "0%", scrollTrigger: { trigger: marker, start: 'top 70%', end: 'bottom 80%' } });
    });
  }
}

function addIdToRecentlyViewed(handle) {
  if (!handle) { let product = document.querySelector('.thb-product-detail'); if (product) handle = product.dataset.handle; }
  if (!handle) return;
  if (window.localStorage) { let recentIds = window.localStorage.getItem('recently-viewed'); if (recentIds != 'undefined' && recentIds != null) window.recentlyViewedIds = JSON.parse(recentIds); }
  var i = window.recentlyViewedIds.indexOf(handle);
  if (i > -1) window.recentlyViewedIds.splice(i, 1);
  window.recentlyViewedIds.unshift(handle);
  if (window.localStorage) window.localStorage.setItem('recently-viewed', JSON.stringify(window.recentlyViewedIds));
}

document.addEventListener('DOMContentLoaded', () => {
  if (typeof AnimatedMarkers !== 'undefined') new AnimatedMarkers();
});